/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';
import { Row, Col, Table, Select } from 'antd';
import usePaginationHook from '../../../../utils/usePagination';
import { useForm, useWatch, Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import 'dayjs/locale/th';
import fitCheckingServices from '../../../../services/fit-checking.services';
import { FiArrowLeft } from 'react-icons/fi';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import MemberInfoCard from '../components/MemberInfoCard';

dayjs.locale('th');

const pageSize = 10;
const currentYear = dayjs().year();
const yearOptions = [];
for (let y = 2026; y <= currentYear; y++) {
  yearOptions.push(y);
}

const monthOptions = [
  { value: '0', label: 'มกราคม' },
  { value: '1', label: 'กุมภาพันธ์' },
  { value: '2', label: 'มีนาคม' },
  { value: '3', label: 'เมษายน' },
  { value: '4', label: 'พฤษภาคม' },
  { value: '5', label: 'มิถุนายน' },
  { value: '6', label: 'กรกฎาคม' },
  { value: '7', label: 'สิงหาคม' },
  { value: '8', label: 'กันยายน' },
  { value: '9', label: 'ตุลาคม' },
  { value: '10', label: 'พฤศจิกายน' },
  { value: '11', label: 'ธันวาคม' },
];

const CheckinUserHistoryPage = () => {
  const history = useHistory();
  const { userId } = useParams();
  const [summary, setSummary] = useState(null);

  const {
    data: historyLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();

  const { control, setValue } = useForm({
    defaultValues: {
      branchId: undefined,
      month: String(dayjs().month()),
      year: currentYear,
    },
  });

  const filterBranchId = useWatch({ control, name: 'branchId' });
  const filterMonth = useWatch({ control, name: 'month' });
  const filterYear = useWatch({ control, name: 'year' });

  fitCheckingServices.useQueryGetUserHistorySummary(
    {
      userId,
      year: filterYear,
      month: filterMonth !== '' ? filterMonth : undefined,
    },
    (data) => {
      setSummary(data);
    },
  );

  fitCheckingServices.useQueryGetCheckinUserHistories(
    {
      take: pageSize,
      page,
      userId,
      branchId: filterBranchId || undefined,
      month: filterMonth !== '' ? filterMonth : undefined,
      year: filterYear || undefined,
    },
    (data) => {
      setData(data.data);
      setTotal(data.meta.total);
    },
  );

  const branchOptions =
    Array.isArray(summary?.branches) && summary.branches.length > 0
      ? summary.branches.map((item, index) => ({
          value: item.branchId || String(index),
          label: item.branchData?.Name || `สาขา ${index + 1}`,
        }))
      : [];

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, _record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'สาขา',
      dataIndex: 'branchName',
      key: 'branchName',
      render: (_, record) => (
        <span>{record.branchData?.Name || record.branchRefId || '-'}</span>
      ),
    },
    {
      title: 'เช็คอินเมื่อ',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (_, record) => (
        <span>
          {record.createdAt
            ? dayjs(record.createdAt).format('DD MMM YYYY HH:mm น.')
            : '-'}
        </span>
      ),
      sorter: (a, b) =>
        dayjs(a.createdAt).diff(dayjs(b.createdAt), 'D') > 0 ? 1 : -1,
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <h3
          onClick={() => {
            history.goBack();
          }}
          className="mr-2"
          style={{ cursor: 'pointer' }}
        >
          <FiArrowLeft />
        </h3>
      </div>

      <Panel>
        <PanelHeader>ประวัติการเช็คอิน</PanelHeader>
        <PanelBody>
          {summary?.user && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              {/* Left: Member Card */}
              <Col span={8}>
                <MemberInfoCard user={summary.user} partner={summary.partner} />
              </Col>

              {/* Right: Summary Cards */}
              <Col span={16}>
                <div
                  style={{
                    display: 'flex',
                    gap: 12,
                    marginBottom: 16,
                    alignItems: 'center',
                    border: '1px solid #d9d9d9',
                    borderRadius: 8,
                    padding: 16,
                  }}
                >
                  ปี:
                  <Controller
                    name="year"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value}
                        onChange={(val) => {
                          onChange(val);
                          setPage(1);
                        }}
                        placeholder="ปี"
                        allowClear
                        style={{ width: 120 }}
                      >
                        <Select.Option value="">ทั้งหมด</Select.Option>
                        {yearOptions.map((y) => (
                          <Select.Option key={y} value={String(y)}>
                            {y}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  />
                  เดือน:
                  <Controller
                    name="month"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value}
                        onChange={(val) => {
                          onChange(val);
                          setValue('year', String(currentYear));
                          setPage(1);
                        }}
                        placeholder="เดือน"
                        allowClear
                        style={{ width: 160 }}
                      >
                        <Select.Option value="">ทั้งหมด</Select.Option>
                        {monthOptions.map((item) => (
                          <Select.Option
                            key={item.value}
                            value={String(item.value)}
                          >
                            {item.label}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  />
                  สาขา:
                  <Controller
                    name="branchId"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value || ''}
                        onChange={(val) => {
                          onChange(val || undefined);
                          setPage(1);
                        }}
                        placeholder="สาขา"
                        allowClear
                        style={{ width: 180 }}
                      >
                        <Select.Option value="">ทั้งหมด</Select.Option>
                        {branchOptions.map((item) => (
                          <Select.Option
                            key={item.value}
                            value={String(item.value)}
                          >
                            {item.label}
                          </Select.Option>
                        ))}
                      </Select>
                    )}
                  />
                </div>
                <Row gutter={[12, 12]}>
                  <Col span={6}>
                    <div
                      style={{
                        backgroundColor: '#eef3ff',
                        borderRadius: 8,
                        padding: 16,
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontSize: 14,
                          color: '#666',
                          marginBottom: 8,
                        }}
                      >
                        จำนวนทั้งหมด
                      </div>
                      <div
                        style={{
                          fontSize: 28,
                          fontWeight: 'bold',
                          color: '#3391ff',
                        }}
                      >
                        {summary?.totalCheckins || 0}
                      </div>
                      <div style={{ fontSize: 12, color: '#999' }}>ครั้ง</div>
                    </div>
                  </Col>
                  {Array.isArray(summary?.branches) &&
                    summary.branches.map((item, index) => (
                      <Col key={index} span={6}>
                        <div
                          style={{
                            border: '1px solid #13C2C2',
                            borderRadius: 8,
                            padding: 16,
                            textAlign: 'center',
                          }}
                        >
                          <div
                            style={{
                              fontSize: 14,
                              color: '#666',
                              marginBottom: 8,
                            }}
                          >
                            {item.branchData?.Name || `สาขา ${index + 1}`}
                          </div>
                          <div style={{ fontSize: 28, fontWeight: 'bold' }}>
                            {item.checkinCount || 0}
                          </div>
                          <div style={{ fontSize: 12, color: '#999' }}>
                            ครั้ง
                          </div>
                        </div>
                      </Col>
                    ))}
                </Row>
              </Col>
            </Row>
          )}

          <Table
            columns={columns}
            dataSource={historyLists}
            style={{ backgroundColor: '#fff' }}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            onChange={({ current }) => {
              setPage(current);
            }}
            pagination={{
              pageSize,
              total,
            }}
          />
        </PanelBody>
      </Panel>
    </>
  );
};

export default CheckinUserHistoryPage;
